/**
 * Grant or decline one credit request, in a modal rather than inline in the
 * card -- the platform owner's own rule for every ops edit surface.
 */

import { useState, type ReactNode } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';

import { defineCatalogue } from '../../i18n';
import type { ApiFailure, ApiFailureError } from '../../lib/http/errors';
import { FormDialog } from '@/chrome/form/FormDialog';
import { Button } from '@/ui/button';
import { useDecideRequest, type DecideRequestInput } from '../api/queries';
import type { CreditRequest } from '../api/schemas';
import { DecisionForm, type Decision } from './DecisionForm';

const catalogue = defineCatalogue(
  {
    'decision.open': 'Decidir',
    'decision.title': 'Decidir este pedido',
    'decision.grant': 'Conceder',
    'decision.decline': 'Recusar',
    'decision.conflict': 'Alguém já decidiu este pedido.',
  },
  {
    'decision.open': 'Decide',
    'decision.title': 'Decide this request',
    'decision.grant': 'Grant',
    'decision.decline': 'Decline',
    'decision.conflict': 'Someone already decided this request.',
  },
);
const t = catalogue.t;

/** Empty clears the amount (the route then falls back to the request's own
 *  `credits_requested`/`slots_requested`); anything else parses as the
 *  integer typed. */
function parseGrantedAmount(raw: string): number | undefined {
  if (raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.trunc(n) : undefined;
}

/** A 409 here always means the same request, so the deck's own sentence
 *  replaces the server's -- which otherwise repeats whatever it said about
 *  the *previous* decision, not this attempt. */
function toDialogFailure(failure: ApiFailure | null): ApiFailure | null {
  if (failure === null || failure.kind !== 'conflict') return failure;
  return { ...failure, message: t('decision.conflict') };
}

export function DecisionDialog(props: {
  request: CreditRequest;
  onDecided?: (request: CreditRequest) => void;
  /** Overrides the live mutation -- how a story drives Pending/Conflict with
   *  no network, the same way `FormDialog`'s own stories drive its states by
   *  prop rather than by a real submit. */
  mutation?: UseMutationResult<CreditRequest, ApiFailureError, DecideRequestInput>;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [decision, setDecision] = useState<Decision | null>(null);
  const [note, setNote] = useState('');
  const requestedAmount =
    props.request.kind === 'SLOTS'
      ? props.request.slots_requested
      : props.request.credits_requested;
  const initialAmount = requestedAmount !== null ? String(requestedAmount) : '';
  const [grantedAmount, setGrantedAmount] = useState(initialAmount);
  const ownMutation = useDecideRequest();
  const decideRequest = props.mutation ?? ownMutation;

  const canSubmit = decision !== null && note.trim().length > 0;

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setDecision(null);
          setNote('');
          setGrantedAmount(initialAmount);
        }
      }}
      trigger={
        <Button type="button" variant="outline" size="sm">
          {t('decision.open')}
        </Button>
      }
      title={t('decision.title')}
      submitLabel={decision === 'DECLINED' ? t('decision.decline') : t('decision.grant')}
      pending={decideRequest.isPending}
      failure={toDialogFailure(decideRequest.error?.failure ?? null)}
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSubmit || decision === null) return;
        const grantedValue = decision === 'GRANTED' ? parseGrantedAmount(grantedAmount) : undefined;
        return decideRequest
          .mutateAsync({
            requestId: props.request.id,
            decision,
            note,
            grantedCredits: props.request.kind === 'CREDITS' ? grantedValue : undefined,
            grantedSlots: props.request.kind === 'SLOTS' ? grantedValue : undefined,
          })
          .then((updated) => props.onDecided?.(updated));
      }}
    >
      <DecisionForm
        kind={props.request.kind}
        decision={decision}
        onDecisionChange={setDecision}
        note={note}
        onNoteChange={setNote}
        grantedAmount={grantedAmount}
        onGrantedAmountChange={setGrantedAmount}
      />
    </FormDialog>
  );
}
